<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <span class="text-muted mr-auto">
                    FTP server <code>{{ bucket.host || '?' }}/{{ bucket.root || '' }}</code>
                    → <code>{{ bucket.folder }}</code>
                    <span v-if="bucket.busy" class="badge badge-warning ml-2" title="An import or a sync holds the lock">import running</span>
                </span>
                <label class="mb-0 mr-2 text-muted">Last</label>
                <select v-model="days" class="form-control form-control-sm mr-2" style="width: 6rem" :disabled="running" @change="loadFiles(false)">
                    <option v-for="n in [7, 30, 90]" :key="n" :value="n">{{ n }} days</option>
                </select>
                <button class="btn btn-sm btn-outline-secondary mr-2" :disabled="running || loadingFiles" @click="loadFiles(true)" title="List the FTP server again (the listing is cached for 5 minutes)">
                    Refresh listing
                </button>
                <button class="btn btn-sm btn-primary" :disabled="running || bucket.busy" @click="syncDays" :title="'Download the files of the last ' + days + ' days that are missing or changed on the server'">
                    <span v-if="running === 'days'">Syncing…</span>
                    <span v-else>Sync last {{ days }} days</span>
                </button>
            </div>
        </div>

        <div class="box">
            <div class="box-content">
                <heading upper="Recent FTP syncs" border>
                    <template v-slot:lower>
                        The last {{ runsBucket.limit || 20 }} sync runs (scheduled hourly, from the command line, or from this page).
                        Filtering and importing the downloaded files is done by the next scheduled import, or from
                        <router-link to="/settings">Settings</router-link>.
                    </template>
                </heading>
                <div v-if="loadingRuns" class="text-center text-muted py-3">Loading…</div>
                <div v-else-if="!runs.length" class="text-center text-muted py-3">No sync runs recorded yet.</div>
                <table v-else class="shx-table w-100 mt-2">
                    <thead>
                        <tr>
                            <th class="shx-th-140">When</th>
                            <th>Run</th>
                            <th class="shx-th-80">Outcome</th>
                            <th class="text-right shx-th-80">Listed</th>
                            <th class="text-right shx-th-100">Downloaded</th>
                            <th class="text-right shx-th-80">Skipped</th>
                            <th class="text-right shx-th-80">Failed</th>
                            <th class="text-right shx-th-80">Seconds</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="run in runs">
                            <tr :key="run.id" @click="toggle(run.id)" :class="run.body ? 'cursor-pointer' : ''">
                                <td>{{ formatDate(run.created_at) }}</td>
                                <td>{{ run.subject.replace(/^FTP sync: /, '') }}<span v-if="run.user_id" class="text-muted"> · user {{ run.user_id }}</span></td>
                                <td><span class="badge" :class="levelClass(run.level)">{{ run.level }}</span></td>
                                <td class="text-right">{{ formatInt(run.gauges['files listed']) }}</td>
                                <td class="text-right">{{ formatInt(run.gauges.downloaded) }}</td>
                                <td class="text-right">{{ formatInt(run.gauges.skipped) }}</td>
                                <td class="text-right" :class="run.gauges.failed ? 'text-danger' : ''">{{ formatInt(run.gauges.failed) }}</td>
                                <td class="text-right">{{ run.gauges.seconds }}</td>
                            </tr>
                            <tr v-if="run.body && expanded === run.id" :key="run.id + '-body'">
                                <td colspan="8"><pre class="small mb-0">{{ run.body }}</pre></td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="box">
            <div class="box-content">
                <heading :upper="'Raw folder vs FTP server, last ' + days + ' days'" border>
                    <template v-slot:lower>
                        One row per day file on the server or in <code>{{ bucket.folder }}</code>.
                        <strong>same</strong>: identical size and not newer on the server ·
                        <strong>remote newer</strong> / <strong>size differs</strong>: the next sync downloads it ·
                        <strong>missing local</strong>: never downloaded ·
                        <strong>local only</strong>: no longer on the server.
                        <em>Resync</em> downloads that one file again, changed or not.
                    </template>
                </heading>
                <div v-if="loadingFiles" class="text-center text-muted py-3">Listing the FTP server…</div>
                <div v-else-if="!files.length" class="text-center text-muted py-3">No day files in the last {{ days }} days.</div>
                <table v-else class="shx-table w-100 mt-2">
                    <thead>
                        <tr>
                            <th>File</th>
                            <th class="shx-th-100">Play date</th>
                            <th class="shx-th-100">Status</th>
                            <th class="text-right shx-th-80">Remote</th>
                            <th class="shx-th-140">Remote modified</th>
                            <th class="text-right shx-th-80">Local</th>
                            <th class="shx-th-140">Local modified</th>
                            <th class="shx-th-80"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in files" :key="row.file">
                            <td>
                                <router-link v-if="row.local_size !== null" :to="'/admissions/sources/' + row.file"><code>{{ row.file }}</code></router-link>
                                <code v-else>{{ row.file }}</code>
                            </td>
                            <td><router-link :to="'/admissions/day?from=' + row.day + '&to=' + row.day">{{ row.day }}</router-link></td>
                            <td><span class="badge" :class="statusClass(row.status)">{{ row.status.replace('_', ' ') }}</span></td>
                            <td class="text-right">{{ row.remote_size !== null ? formatSize(row.remote_size) : '—' }}</td>
                            <td>{{ formatDate(row.remote_modified) }}</td>
                            <td class="text-right">{{ row.local_size !== null ? formatSize(row.local_size) : '—' }}</td>
                            <td>{{ formatDate(row.local_modified) }}</td>
                            <td class="text-right">
                                <button v-if="row.remote_size !== null" class="btn btn-sm btn-outline-primary" :disabled="running || bucket.busy" @click="syncFile(row)">
                                    <span v-if="running === row.file">…</span>
                                    <span v-else>Resync</span>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Heading from '../../partials/Heading';

export default {
    name: 'AdmissionsSync',
    components: {Heading},
    data() {
        return {
            loadingRuns: true,
            loadingFiles: true,
            running: null,
            days: 30,
            bucket: {},
            runsBucket: {},
            runs: [],
            files: [],
            expanded: null,
        };
    },
    created() {
        this.loadRuns();
        this.loadFiles(false);
    },
    methods: {
        loadRuns() {
            this.loadingRuns = true;
            return axios
                .get('/admissions/sync/runs')
                .then(response => {
                    this.runsBucket = response.data.data.bucket || {};
                    this.runs = response.data.data.rows || [];
                    this.bucket = Object.assign({}, this.bucket, this.runsBucket);
                })
                .catch(error => this.fail(error))
                .finally(() => {
                    this.loadingRuns = false;
                });
        },
        loadFiles(refresh) {
            this.loadingFiles = true;
            return axios
                .get('/admissions/sync/files', {params: {days: this.days, refresh: refresh ? 1 : 0}})
                .then(response => {
                    this.bucket = Object.assign({}, this.bucket, response.data.data.bucket || {});
                    this.files = response.data.data.rows || [];
                })
                .catch(error => this.fail(error))
                .finally(() => {
                    this.loadingFiles = false;
                });
        },
        syncDays() {
            this.running = 'days';
            return axios
                .post('/admissions/sync/run', {days: this.days})
                .then(response => {
                    const result = response.data.data.result;
                    this.$toast.success(`${result.downloaded} downloaded, ${result.skipped} unchanged`);
                })
                .catch(error => this.fail(error))
                .finally(() => this.reload());
        },
        syncFile(row) {
            this.running = row.file;
            return axios
                .post('/admissions/sync/files/' + row.file)
                .then(response => {
                    const updated = response.data.data.row;
                    if (updated) {
                        this.files = this.files.map(f => f.file === updated.file ? updated : f);
                    }
                    this.$toast.success(row.file + ' downloaded');
                })
                .catch(error => this.fail(error))
                .finally(() => this.reload(false));
        },
        reload(files = true) {
            this.running = null;
            this.loadRuns();
            if (files) {
                this.loadFiles(false);
            }
        },
        fail(error) {
            const message = error.response?.data?.error?.message || error.message;
            if (error.response?.status === 409) {
                this.$toast.warning('An import is running, try again later');
                this.bucket = Object.assign({}, this.bucket, {busy: true});
            } else {
                this.$toast.error(message);
            }
        },
        toggle(id) {
            this.expanded = this.expanded === id ? null : id;
        },
        levelClass(level) {
            return {info: 'badge-success', warning: 'badge-warning', error: 'badge-danger'}[level] || 'badge-secondary';
        },
        statusClass(status) {
            return {
                same: 'badge-success',
                remote_newer: 'badge-warning',
                size_differs: 'badge-warning',
                missing_local: 'badge-danger',
                local_only: 'badge-secondary',
            }[status] || 'badge-secondary';
        },
        formatInt(v) {
            return v === undefined || v === null ? '—' : Math.round(v).toLocaleString();
        },
        formatSize(bytes) {
            if (bytes >= 1024 * 1024) {
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            }
            return Math.round(bytes / 1024) + ' kB';
        },
        formatDate(iso) {
            if (!iso) {
                return '—';
            }
            return iso.replace('T', ' ').substring(0, 16);
        },
    },
};
</script>
